/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  ExpandMore,
  Recycling,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

const CATEGORIAS = [
  { value: 'papelao', label: 'Papelão' },
  { value: 'lata', label: 'Lata' },
  { value: 'mobiliario', label: 'Mobiliário' },
  { value: 'outros', label: 'Outros' },
];

function formatCurrency(v) {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getCategoriaLabel(categoria) {
  return CATEGORIAS.find((item) => item.value === categoria)?.label || categoria || '-';
}

export default function Reciclagem() {
  const { token, user, isAdmin } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [dataLancamento, setDataLancamento] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const [filtroClienteId, setFiltroClienteId] = useState('all');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  const [itens, setItens] = useState([
    { categoria: 'papelao', outro_especificacao: '', valor: '' },
  ]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const resumo = useMemo(() => {
    const totalLancamentos = lancamentos.length;
    const totalItens = lancamentos.reduce((acc, lanc) => acc + (lanc.itens?.length || 0), 0);
    const valorTotal = lancamentos.reduce((acc, lanc) => acc + Number(lanc.total || 0), 0);
    const ticketMedio = totalLancamentos > 0 ? valorTotal / totalLancamentos : 0;

    const porCategoriaMap = {};
    lancamentos.forEach((lanc) => {
      (lanc.itens || []).forEach((item) => {
        const key = item.categoria || 'outros';
        if (!porCategoriaMap[key]) {
          porCategoriaMap[key] = {
            categoria: key,
            label: getCategoriaLabel(key),
            quantidade: 0,
            valor: 0,
          };
        }

        porCategoriaMap[key].quantidade += 1;
        porCategoriaMap[key].valor += Number(item.valor || 0);
      });
    });

    const categorias = Object.values(porCategoriaMap).sort((a, b) => b.valor - a.valor);

    return {
      totalLancamentos,
      totalItens,
      valorTotal,
      ticketMedio,
      categorias,
    };
  }, [lancamentos]);

  const loadClientes = async () => {
    if (!token) return;

    try {
      let allowedClienteIds = null;

      const meRes = await fetch(`${API}/me/clientes`, { headers: authHeaders });
      if (meRes.ok) {
        const meData = await meRes.json();
        allowedClienteIds = meData.clientes || [];
      }

      const res = await fetch(`${API}/clientes`, { headers: authHeaders });
      if (!res.ok) return;

      let data = await res.json();
      if (allowedClienteIds && allowedClienteIds.length > 0) {
        data = data.filter((c) => allowedClienteIds.map(Number).includes(Number(c.id)));
      }

      setClientes(data);
      if (!clienteId && data.length > 0) {
        setClienteId(data[0].id);
        // filtroClienteId keeps "" so filter defaults to "Todos"
      }
    } catch (err) {
      console.error('Erro ao carregar clientes', err);
    }
  };

  const loadLancamentos = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroClienteId && filtroClienteId !== 'all') params.append('cliente_id', filtroClienteId);
      if (filtroDataInicio) params.append('data_inicio', filtroDataInicio);
      if (filtroDataFim) params.append('data_fim', filtroDataFim);

      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${API}/reciclagem-despesas${query}`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setLancamentos(Array.isArray(data) ? data : []);
      } else {
        setLancamentos([]);
      }
    } catch (err) {
      console.error('Erro ao carregar lançamentos', err);
      setLancamentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadLancamentos();
  }, [token, filtroClienteId, filtroDataInicio, filtroDataFim]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => {
    setItens((prev) => [...prev, { categoria: 'papelao', outro_especificacao: '', valor: '' }]);
  };

  const removeItem = (index) => {
    setItens((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItens((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const validarFormulario = () => {
    if (!clienteId) {
      setSnack({ open: true, message: 'Selecione o cliente.', severity: 'warning' });
      return false;
    }

    if (!dataLancamento) {
      setSnack({ open: true, message: 'Informe a data.', severity: 'warning' });
      return false;
    }

    if (!itens.length) {
      setSnack({ open: true, message: 'Inclua ao menos uma despesa.', severity: 'warning' });
      return false;
    }

    for (let i = 0; i < itens.length; i += 1) {
      const it = itens[i];
      if (!it.categoria) {
        setSnack({ open: true, message: `Item ${i + 1}: selecione a categoria.`, severity: 'warning' });
        return false;
      }
      if (it.categoria === 'outros' && !String(it.outro_especificacao || '').trim()) {
        setSnack({ open: true, message: `Item ${i + 1}: especifique o campo Outros.`, severity: 'warning' });
        return false;
      }
      if (!it.valor || Number(it.valor) <= 0) {
        setSnack({ open: true, message: `Item ${i + 1}: informe valor válido.`, severity: 'warning' });
        return false;
      }
    }

    return true;
  };

  const salvarLancamento = async () => {
    if (!validarFormulario()) return;

    try {
      const res = await fetch(`${API}/reciclagem-despesas/lancamentos`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cliente_id: clienteId,
          data: dataLancamento,
          itens: itens.map((it) => ({
            categoria: it.categoria,
            outro_especificacao: it.categoria === 'outros' ? it.outro_especificacao : null,
            valor: it.valor,
          })),
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSnack({ open: true, message: payload.error || 'Falha ao salvar lançamento.', severity: 'error' });
        return;
      }

      setSnack({ open: true, message: 'Lançamento de reciclagem salvo com sucesso.', severity: 'success' });
      setItens([{ categoria: 'papelao', outro_especificacao: '', valor: '' }]);
      loadLancamentos();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: 'Erro ao salvar lançamento.', severity: 'error' });
    }
  };

  const excluirLancamento = async (lancamentoUuid) => {
    if (!window.confirm('Confirma exclusão do lançamento completo?')) return;

    try {
      const res = await fetch(`${API}/reciclagem-despesas/lancamentos/${lancamentoUuid}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        setSnack({ open: true, message: payload.error || 'Erro ao excluir lançamento.', severity: 'error' });
        return;
      }

      setSnack({ open: true, message: 'Lançamento excluído.', severity: 'success' });
      loadLancamentos();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: 'Erro ao excluir lançamento.', severity: 'error' });
    }
  };

  const excluirItemLancado = async (itemId) => {
    if (!window.confirm('Confirma exclusão deste item?')) return;

    try {
      const res = await fetch(`${API}/reciclagem-despesas/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        setSnack({ open: true, message: payload.error || 'Erro ao excluir item.', severity: 'error' });
        return;
      }

      setSnack({ open: true, message: 'Item excluído.', severity: 'success' });
      loadLancamentos();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: 'Erro ao excluir item.', severity: 'error' });
    }
  };

  const podeExcluirItemLancado = (lancamento, item) => {
    if (isAdmin()) return true;

    const userId = Number(user?.id || 0);
    const itemCriadoPor = Number(item?.criado_por || 0);
    const lancamentoCriadoPor = Number(lancamento?.criado_por || 0);
    const userNome = String(user?.nome || user?.username || '').trim().toLowerCase();
    const criadorNome = String(lancamento?.criador_nome || '').trim().toLowerCase();

    if (userId > 0 && (userId === itemCriadoPor || userId === lancamentoCriadoPor)) {
      return true;
    }

    return userNome && criadorNome && userNome === criadorNome;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Recycling color="success" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Reciclagem</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Despesas isoladas do financeiro principal. Estes lançamentos não entram em contratos, despesas gerais ou resumos financeiros do sistema.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary">Total de lançamentos</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{resumo.totalLancamentos}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary">Total de itens</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{resumo.totalItens}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary">Valor total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {formatCurrency(resumo.valorTotal)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary">Ticket médio</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatCurrency(resumo.ticketMedio)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 1,
            pt: 2,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Total de resultados:
            </Typography>
            <Chip label={`${resumo.totalLancamentos} lançamento(s)`} size="small" color="primary" />
          </Box>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            Valor Total: {formatCurrency(resumo.valorTotal)}
          </Typography>
        </Box>

        <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3, bgcolor: '#f9f9f9', mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filtros</Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              select
              label="Cliente"
              value={filtroClienteId}
              onChange={(e) => setFiltroClienteId(e.target.value)}
              sx={{ minWidth: 160, maxWidth: 320 }}
              SelectProps={{ autoWidth: true }}
            >
              <MenuItem value="all">TODOS OS CLIENTES</MenuItem>
              {clientes.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Data Inicial"
              value={filtroDataInicio}
              onChange={(e) => setFiltroDataInicio(e.target.value)}
              sx={{ flex: '0 0 170px' }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="Data Final"
              value={filtroDataFim}
              onChange={(e) => setFiltroDataFim(e.target.value)}
              sx={{ flex: '0 0 170px' }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Paper>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Itens</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Valor Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Participação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resumo.categorias.map((categoria) => {
                const percentual = resumo.valorTotal > 0 ? (categoria.valor / resumo.valorTotal) * 100 : 0;
                return (
                  <TableRow key={categoria.categoria} hover>
                    <TableCell>{categoria.label}</TableCell>
                    <TableCell>{categoria.quantidade}</TableCell>
                    <TableCell align="right">{formatCurrency(categoria.valor)}</TableCell>
                    <TableCell align="right">{percentual.toFixed(1)}%</TableCell>
                  </TableRow>
                );
              })}
              {resumo.categorias.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nenhum lançamento disponível para consolidar.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Novo Lançamento</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Cliente"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">— Todos —</MenuItem>
              {clientes.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="Data"
              value={dataLancamento}
              onChange={(e) => setDataLancamento(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {itens.map((item, index) => (
          <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={`Categoria ${index + 1}`}
                value={item.categoria}
                onChange={(e) => updateItem(index, 'categoria', e.target.value)}
                fullWidth
              >
                {CATEGORIAS.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Especificação (quando Outros)"
                value={item.outro_especificacao}
                onChange={(e) => updateItem(index, 'outro_especificacao', e.target.value)}
                fullWidth
                disabled={item.categoria !== 'outros'}
              />
            </Grid>
            <Grid item xs={10} md={3}>
              <TextField
                label="Valor"
                type="number"
                value={item.valor}
                onChange={(e) => updateItem(index, 'valor', e.target.value)}
                fullWidth
                inputProps={{ step: '0.01', min: 0 }}
              />
            </Grid>
            <Grid item xs={2} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton
                color="error"
                onClick={() => removeItem(index)}
                disabled={itens.length <= 1}
              >
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Add />} onClick={addItem}>
            Novo lançamento
          </Button>
          <Button variant="contained" color="success" onClick={salvarLancamento}>
            Salvar lançamento
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Lançamentos</Typography>

        {loading && <Alert severity="info" sx={{ mb: 2 }}>Carregando lançamentos...</Alert>}
        {!loading && lancamentos.length === 0 && (
          <Alert severity="warning">Nenhum lançamento encontrado para o filtro atual.</Alert>
        )}

        {lancamentos.map((lanc) => (
          <Accordion key={lanc.lancamento_uuid} sx={{ mb: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{lanc.cliente_nome}</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Chip label={new Date(lanc.data + 'T00:00:00').toLocaleDateString('pt-BR')} size="small" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">{lanc.itens.length} item(ns)</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(lanc.total)}</Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Criado por: {lanc.criador_nome || 'Usuário'}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Categoria</TableCell>
                      <TableCell>Especificação</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      {lanc.itens.some((it) => podeExcluirItemLancado(lanc, it)) && <TableCell align="right">Ações</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lanc.itens.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>
                          {getCategoriaLabel(it.categoria)}
                        </TableCell>
                        <TableCell>{it.outro_especificacao || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(it.valor)}</TableCell>
                        {lanc.itens.some((itemLancado) => podeExcluirItemLancado(lanc, itemLancado)) && (
                          <TableCell align="right">
                            {podeExcluirItemLancado(lanc, it) && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => excluirItemLancado(it.id)}
                                aria-label="Excluir item"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {isAdmin() && (
                <Box sx={{ mt: 1.5 }}>
                  <Button color="error" onClick={() => excluirLancamento(lanc.lancamento_uuid)}>
                    Excluir lançamento
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
